/**
 * SessionsDialog - unified dialog for session management
 *
 * Shows:
 * 1. Current session (if loaded) with Resume option
 * 2. Start fresh session option
 * 3. All sessions (Pro mode) - collapsible section with session list and management
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Clock, Layers, Plus, Trash2, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { RandomDotmSpinner } from '../../../components/ui/RandomDotmSpinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { SessionListCard } from './SessionListCard';
import { FeaturedSessionCard } from './FeaturedSessionCard';
import { DeleteSessionConfirmDialog } from './DeleteSessionConfirmDialog';
import { SessionNameEditingProvider } from './sessionNameEditingContext';
import { useSessionStore } from '../store/sessionStore';
import { useSessionsUIStore } from '../store/uiStore';
import { useAppStore } from '../../../store/appStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { useHasProAccess, useProAccessReady, ProBadge, UpgradeDialog } from '../../../core/licensing';
import { toast } from '../../../components/ui/toaster';
import {
  getConnectedConnectionIds,
  getConnectionInfo,
  formatBytes,
  getPersistedSessionsStorageBytes,
  isSessionLiveInWorkspace,
} from '../utils/sessionDisplayUtils';
import { useAppVersion } from '../../../hooks/useAppVersion';

interface SessionsDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /**
   * Called when resume is clicked (for current session). Receives the
   * connections the user switched off on the featured card — the resume must
   * neither connect them nor keep them in the session.
   */
  onResume: (excludedConnectionIds?: string[]) => void;
  /** Called when a different session is opened (Pro mode) */
  onOpenSession?: (sessionId: string, excludedConnectionIds?: string[]) => void;
  /** Called when start fresh is clicked */
  onStartFresh: () => void;
  /** Called when new session is created (Pro mode) */
  onNewSession?: () => void;
  /** Called when dialog is closed without action (optional) */
  onClose?: () => void;
}

export function SessionsDialog({
  open,
  onResume,
  onOpenSession,
  onStartFresh,
  onNewSession,
  onClose,
}: SessionsDialogProps) {
  const {
    currentSession,
    hasSavedSession,
    getSavedSessions,
    deleteSession,
    deleteAllSessions,
    deleteSessionsOlderThan,
    renameSession,
  } = useSessionStore();
  const isStartupDialog = useSessionsUIStore((state) => state.isStartupDialog);
  const startupSessionBehavior = useAppStore((state) => state.startupSessionBehavior);
  const setStartupSessionBehavior = useAppStore((state) => state.setStartupSessionBehavior);
  const connections = useConnectionStore((state) => state.connections);
  const activeConnections = useConnectionStore((state) => state.activeConnections);
  const workspaceTabs = useWorkspaceStore((state) => state.tabs);
  const isProMode = useHasProAccess();
  const isProAccessReady = useProAccessReady();

  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [showDeleteOlderConfirm, setShowDeleteOlderConfirm] = useState(false);
  const [deleteOlderDaysInput, setDeleteOlderDaysInput] = useState('10');
  const [isAllSessionsExpanded, setIsAllSessionsExpanded] = useState(false);
  const [isSessionNameEditing, setIsSessionNameEditing] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  /** Connections switched off on the featured card, for the next resume only. */
  const [excludedConnectionIds, setExcludedConnectionIds] = useState<string[]>([]);
  const { track } = useAnalytics();
  // Set by onEscapeKeyDown/onPointerDownOutside just before Radix calls
  // onOpenChange(false), so handleOpenChange knows which route to attribute
  // the dismissal to. Cleared after each read.
  const dismissReasonRef = useRef<'esc' | 'outside' | null>(null);

  const hasSession = hasSavedSession();
  const savedSessions = getSavedSessions();
  const storageBytes = getPersistedSessionsStorageBytes();
  const appVersion = useAppVersion();

  useEffect(() => {
    if (open && isProMode) {
      setIsAllSessionsExpanded(false);
    }
  }, [open, isProMode]);

  useEffect(() => {
    if (!open) {
      setIsSessionNameEditing(false);
    }
  }, [open]);

  // Sessions opened more than 7 days ago are hidden from the top of the dialog
  // (last-session card + earlier sessions) and only surface in the "All Sessions"
  // expander, to keep the surfaced shortcuts focused on what the user actually uses.
  const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
  const isRecentlyUsed = (lastOpenedAt: string) =>
    Date.now() - new Date(lastOpenedAt).getTime() < RECENT_WINDOW_MS;

  // Get the most recent non-empty session that isn't the current one (for "Reopen last session")
  // A session is considered empty if it has no connections AND only has start-page tabs (or no tabs)
  const lastSession = savedSessions.find((s) => {
    if (s.id === currentSession?.id) return false;
    if (!isRecentlyUsed(s.lastOpenedAt)) return false;
    const hasConnections = s.connections.length > 0;
    const hasMeaningfulTabs = s.tabs.some(tab => tab.type !== 'start-page');
    return hasConnections || hasMeaningfulTabs;
  });

  // The session displayed as the big "last session" card at the top of the dialog.
  // When the user is mid-session it's the current session; on startup it's the most
  // recent saved session.
  const featuredSessionId = currentSession?.id ?? lastSession?.id;

  // The next 2 most recently used sessions (within the 7-day window), shown as
  // compact cards below the featured "last session" card. Pro mode only —
  // personal mode has at most one saved session.
  const earlierSessions = useMemo(
    () =>
      savedSessions
        .filter((s) => s.id !== featuredSessionId && isRecentlyUsed(s.lastOpenedAt))
        .slice(0, 2),
    [savedSessions, featuredSessionId]
  );

  // Empty state — nothing to restore at the top of the dialog and (in Pro)
  // no saved sessions at all to surface in the All Sessions expander.
  // Uses `hasSession` (not `currentSession`) because the current-session card
  // is gated on hasSession too — an "empty" current session shouldn't count.
  const showEmptyState =
    !hasSession &&
    !lastSession &&
    earlierSessions.length === 0 &&
    (!isProMode || savedSessions.length === 0);

  // Get connection info for current session
  const currentSessionConnectionInfo = useMemo(() => {
    if (!currentSession) return [];
    return getConnectionInfo(currentSession.tabs, currentSession.connections, connections);
  }, [currentSession, connections]);

  // Get connection info for last session (when no current session)
  const lastSessionConnectionInfo = useMemo(() => {
    if (!lastSession) return [];
    return getConnectionInfo(lastSession.tabs, lastSession.connections, connections);
  }, [lastSession, connections]);

  // Is the current session actually the live workspace right now?
  const isCurrentSessionLive = useMemo(
    () =>
      currentSession
        ? isSessionLiveInWorkspace(
            currentSession,
            workspaceTabs.map((tab) => tab.id),
            // Status-filtered on purpose: a dropped connection keeps its
            // `activeConnections` entry, and counting it would make the card
            // offer "go back" to a session that isn't there.
            getConnectedConnectionIds(activeConnections)
          )
        : false,
    [currentSession, workspaceTabs, activeConnections]
  );

  // What the featured card's primary action means. Closing the dialog is only
  // honest when the session is already restored — otherwise the card says
  // "resume" and nothing comes back (the startup dialog is always a resume:
  // nothing has been restored yet at that point).
  const featuredCardResumes = isStartupDialog || !isCurrentSessionLive;

  // Exclusions are scoped to one card, one resume: forget them whenever the
  // dialog opens/closes or the featured session changes underneath us (E2's
  // eager prune can delete an emptied session mid-render).
  useEffect(() => {
    setExcludedConnectionIds([]);
  }, [open, featuredSessionId]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setIsSessionNameEditing(false);
      // Radix calls onOpenChange(false) for Esc and outside-click dismissals
      // only (the X button below closes via onClose directly, bypassing this
      // path, so it can't double-fire here). Attribute using whichever
      // guard ran just before this — default to 'outside' if neither fired
      // (defensive; both paths currently always set it first).
      track('feature:sessions_dialog_dismiss', { via: dismissReasonRef.current ?? 'outside' });
      dismissReasonRef.current = null;
    }
    if (!isOpen && onClose) {
      onClose();
    }
  };

  const handleCloseButtonClick = () => {
    setIsSessionNameEditing(false);
    track('feature:sessions_dialog_dismiss', { via: 'x' });
    onClose?.();
  };

  const handleToggleConnectionExcluded = (connectionId: string, excluded: boolean) => {
    setExcludedConnectionIds((prev) =>
      excluded
        ? prev.includes(connectionId)
          ? prev
          : [...prev, connectionId]
        : prev.filter((id) => id !== connectionId)
    );
    // Toggle → fires on both ON and OFF. `enabled` is the exclusion itself:
    // true = this connection will NOT be restored. No connection id/name.
    track('feature:session_connection_exclude', { enabled: excluded });
  };

  const handleReturnToCurrentSession = () => {
    setIsSessionNameEditing(false);

    // Only a session that is already live may be "returned to" by closing the
    // dialog. Any other state (startup, or a session the user dismissed and
    // came back to from the toolbar) has to actually restore — otherwise the
    // card promises a resume and silently does nothing.
    if (featuredCardResumes) {
      onResume(excludedConnectionIds);
      return;
    }

    onClose?.();
  };

  const handleOpenSession = (sessionId: string, excluded: string[] = []) => {
    if (sessionId === currentSession?.id) {
      handleReturnToCurrentSession();
    } else if (onOpenSession) {
      onOpenSession(sessionId, excluded);
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    deleteSession(sessionId);
  };

  const handleRequestDeleteSession = (sessionId: string, sessionName: string) => {
    if (sessionId === currentSession?.id) {
      toast.error({
        title: 'Cannot delete current session',
        description: 'Switch to a different session first.',
      });
      return;
    }
    setPendingDelete({ id: sessionId, name: sessionName });
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    handleDeleteSession(pendingDelete.id);
    setPendingDelete(null);
  };

  const handleNewSession = () => {
    if (isProMode && onNewSession) {
      onNewSession();
      return;
    }
    onStartFresh();
  };

  const handleDeleteAllSessions = async () => {
    useWorkspaceStore.getState().restoreTabs([], null);

    const { activeConnections, disconnectFromDatabase } = useConnectionStore.getState();
    for (const conn of activeConnections) {
      await disconnectFromDatabase(conn.id);
    }

    deleteAllSessions();
    setShowDeleteAllConfirm(false);
  };

  const handleDeleteOlderSessions = () => {
    const days = Math.max(1, Math.min(365, parseInt(deleteOlderDaysInput) || 10));
    const deletedCount = deleteSessionsOlderThan(days);
    setShowDeleteOlderConfirm(false);
    if (deletedCount > 0) {
      toast.success({
        title: `Deleted ${deletedCount} session${deletedCount === 1 ? '' : 's'}`,
        description: `Sessions not used in the last ${days} day${days === 1 ? '' : 's'} have been removed.`,
      });
    } else {
      toast.default({
        title: 'No sessions deleted',
        description: `No sessions were older than ${days} day${days === 1 ? '' : 's'}.`,
      });
    }
  };

  const getSessionsOlderThanCount = (days: number) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return savedSessions.filter((session) => {
      if (currentSession?.id === session.id) return false;
      const lastOpened = new Date(session.lastOpenedAt);
      return lastOpened < cutoffDate;
    }).length;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[650px] max-h-[90vh] flex flex-col"
        data-testid="sessions-dialog"
        onEscapeKeyDown={(event) => {
          if (isSessionNameEditing) {
            event.preventDefault();
            return;
          }
          dismissReasonRef.current = 'esc';
        }}
        onPointerDownOutside={() => {
          dismissReasonRef.current = 'outside';
        }}
      >
        <SessionNameEditingProvider value={{ setIsEditing: setIsSessionNameEditing }}>
        <DialogHeader className="flex-shrink-0 flex-row items-center justify-between space-y-0">
          <DialogTitle>Sessions</DialogTitle>
          <div className="flex items-center gap-2">
            {isProAccessReady && !showEmptyState && (
              <Button
                onClick={handleNewSession}
                size="sm"
                className="active:scale-[0.96] transition-[color,box-shadow,scale]"
                data-testid={isProMode ? 'new-session-button' : 'start-fresh-button'}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {isProMode ? 'New Session' : 'Start Fresh'}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={handleCloseButtonClick}
              data-testid="sessions-dialog-close-button"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="py-4 space-y-4 flex-1 overflow-y-auto min-h-0">
          {/* Loading state — wait until Pro vs Personal is known so we don't
              flash the Personal layout before the trial/subscription resolves. */}
          {!isProAccessReady && (
            <div
              className="flex flex-col items-center justify-center py-10 px-4 text-center"
              data-testid="sessions-loading"
            >
              <div className="text-muted-foreground mb-3">
                <RandomDotmSpinner size={24} />
              </div>
              <p className="text-sm text-muted-foreground">Loading sessions…</p>
            </div>
          )}

          {/* Empty state — no sessions to restore */}
          {isProAccessReady && showEmptyState && (
            <div
              className="flex flex-col items-center justify-center py-10 px-4 text-center"
              data-testid="sessions-empty-state"
            >
              <p className="text-sm text-muted-foreground mb-4">
                No sessions to restore yet.
              </p>
              <Button
                onClick={handleNewSession}
                size="sm"
                className="active:scale-[0.96] transition-[color,box-shadow,scale]"
                data-testid={isProMode ? 'new-session-button' : 'start-fresh-button'}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {isProMode ? 'Start a new session' : 'Start a fresh session'}
              </Button>
            </div>
          )}

          {/* Last Session - the user's current/most-recent session shown as a big card */}
          {isProAccessReady && hasSession && currentSession && (
            <FeaturedSessionCard
              session={currentSession}
              title={featuredCardResumes ? 'Resume Last Session' : 'Go back to your session'}
              connections={currentSessionConnectionInfo}
              savedConnections={connections}
              excludedConnectionIds={excludedConnectionIds}
              canExcludeConnections={featuredCardResumes}
              onToggleConnectionExcluded={handleToggleConnectionExcluded}
              onActivate={handleReturnToCurrentSession}
              actionLabel={featuredCardResumes ? 'Resume' : 'Go back'}
              onRename={(name) => renameSession(currentSession.id, name)}
              testId="resume-session-button"
            />
          )}

          {/* Reopen Last Session - shown when no current session but there are saved sessions */}
          {isProAccessReady && !currentSession && lastSession && (
            <FeaturedSessionCard
              session={lastSession}
              title="Reopen Last Session"
              connections={lastSessionConnectionInfo}
              savedConnections={connections}
              excludedConnectionIds={excludedConnectionIds}
              // A session that isn't the current one is never the live
              // workspace — opening it always restores.
              canExcludeConnections
              onToggleConnectionExcluded={handleToggleConnectionExcluded}
              onActivate={() => handleOpenSession(lastSession.id, excludedConnectionIds)}
              actionLabel="Open"
              onRename={(name) => renameSession(lastSession.id, name)}
              testId="reopen-last-session-button"
            />
          )}

          {/* Earlier sessions - up to 2 most recently used sessions besides the featured one (Pro only) */}
          {isProAccessReady && isProMode && earlierSessions.length > 0 && (
            <div
              className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-200 delay-75 fill-mode-backwards"
              data-testid="earlier-sessions-list"
            >
              {earlierSessions.map((session) => (
                <SessionListCard
                  key={session.id}
                  session={session}
                  connections={connections}
                  isActive={session.id === currentSession?.id}
                  onOpen={() => handleOpenSession(session.id)}
                  onRename={(name) => renameSession(session.id, name)}
                  onDelete={() => handleRequestDeleteSession(session.id, session.name)}
                />
              ))}
            </div>
          )}

          {/* All Sessions - Collapsible (Pro mode only) */}
          {isProAccessReady && isProMode && savedSessions.length > 0 && (
            <div className="border-t border-border pt-4 animate-in fade-in slide-in-from-bottom-2 duration-200 delay-150 fill-mode-backwards">
              {/* Expander Header */}
              <button
                onClick={() => setIsAllSessionsExpanded((prev) => !prev)}
                className="w-full flex items-center justify-between text-left hover:bg-muted/50 rounded-md p-2 -m-2 transition-colors"
                data-testid="all-sessions-expander"
              >
                <div className="flex items-center gap-2">
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 text-muted-foreground transition-transform duration-150',
                      isAllSessionsExpanded && 'rotate-90',
                    )}
                  />
                  <span className="text-sm font-medium">All Sessions</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                  <span data-testid="sessions-total-count">
                    {savedSessions.length} session{savedSessions.length === 1 ? '' : 's'}
                  </span>
                  <span data-testid="sessions-storage-used">{formatBytes(storageBytes)}</span>
                </div>
              </button>

              {/* Expanded Content */}
              {isAllSessionsExpanded && (
                <div className="mt-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-150">
                  {/* Delete Buttons */}
                  {savedSessions.length > 1 && (
                    <div className="flex justify-end gap-2">
                      {savedSessions.length > 3 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setShowDeleteOlderConfirm(true)}
                          data-testid="sessions-dialog-delete-older-button"
                        >
                          <Clock className="h-4 w-4 mr-2" />
                          Delete Older Than...
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setShowDeleteAllConfirm(true)}
                        data-testid="sessions-dialog-delete-all-button"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete All
                      </Button>
                    </div>
                  )}

                  {/* Session List */}
                  <div
                    className="space-y-2"
                    data-testid="sessions-list"
                  >
                    {savedSessions.map((session) => (
                      <SessionListCard
                        key={session.id}
                        session={session}
                        connections={connections}
                        isActive={session.id === currentSession?.id}
                        onOpen={() => handleOpenSession(session.id)}
                        onRename={(name) => renameSession(session.id, name)}
                        onDelete={() => handleRequestDeleteSession(session.id, session.name)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pro Mode Upsell - Personal mode only */}
          {isProAccessReady && !isProMode && (
            <div className="mt-4 pt-4 border-t border-border animate-in fade-in slide-in-from-bottom-2 duration-200 delay-150 fill-mode-backwards">
              <div
                role="button"
                tabIndex={0}
                onClick={() => setShowUpgradeDialog(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowUpgradeDialog(true);
                  }
                }}
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/60 transition-colors cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-ring/30 focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                data-testid="sessions-dialog-upgrade-upsell"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary shrink-0">
                  <Layers className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium">Multiple Sessions</span>
                    <ProBadge size="sm" />
                  </div>
                  <p className="text-xs text-muted-foreground text-pretty">
                    Upgrade to Pro to save and switch between multiple workspaces.
                    Keep different projects organized and pick up right where you left off.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DeleteSessionConfirmDialog
          open={pendingDelete !== null}
          sessionName={pendingDelete?.name ?? ''}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleConfirmDelete}
        />

        <AlertDialog open={showDeleteAllConfirm} onOpenChange={setShowDeleteAllConfirm}>
          <AlertDialogContent data-testid="sessions-dialog-delete-all-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all sessions?</AlertDialogTitle>
              <AlertDialogDescription className="text-pretty">
                This will delete {savedSessions.length} session{savedSessions.length === 1 ? '' : 's'}, close all tabs, and disconnect from all servers. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllSessions}
                className="bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill/90"
              >
                Delete All Sessions
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={showDeleteOlderConfirm} onOpenChange={setShowDeleteOlderConfirm}>
          <DialogContent data-testid="sessions-dialog-delete-older-confirm">
            <DialogHeader>
              <DialogTitle>Delete old sessions?</DialogTitle>
              <DialogDescription className="text-pretty">
                Delete sessions that haven&apos;t been used in the specified number of days. The current session will not be deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="dialog-delete-older-days" className="text-sm font-medium">
                Delete sessions older than
              </Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  id="dialog-delete-older-days"
                  type="number"
                  min={1}
                  max={365}
                  value={deleteOlderDaysInput}
                  onChange={(e) => setDeleteOlderDaysInput(e.target.value)}
                  className="w-20"
                  data-testid="sessions-dialog-delete-older-days-input"
                />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2 tabular-nums" data-testid="sessions-dialog-delete-older-count">
                {getSessionsOlderThanCount(parseInt(deleteOlderDaysInput) || 10)} session{getSessionsOlderThanCount(parseInt(deleteOlderDaysInput) || 10) === 1 ? '' : 's'} will be deleted
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteOlderConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteOlderSessions}
              >
                Delete Sessions
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </SessionNameEditingProvider>

        <UpgradeDialog
          open={showUpgradeDialog}
          onOpenChange={setShowUpgradeDialog}
          feature="session-management"
        />

        {/* Startup-only escape hatch. The checkbox IS the persisted setting
            (mirrored in Settings → Sessions), not a pending choice — flipping
            it takes effect on the next startup regardless of how this dialog
            is closed. */}
        {isStartupDialog && (
          <div className="flex-shrink-0 flex items-center gap-2 pt-3 border-t border-border">
            <Checkbox
              id="always-start-new-session"
              checked={startupSessionBehavior === 'new-session'}
              onCheckedChange={(checked) => {
                const enabled = checked === true;
                setStartupSessionBehavior(enabled ? 'new-session' : 'ask');
                track('feature:sessions_always_start_new_toggled', {
                  enabled,
                  via: 'startup_dialog',
                });
              }}
              data-testid="always-start-new-session-checkbox"
            />
            <Label
              htmlFor="always-start-new-session"
              className="text-xs font-normal text-muted-foreground cursor-pointer"
            >
              Always start a new session
              <span className="ml-1 opacity-70">
                (skips this dialog on startup — change anytime in Settings → Sessions)
              </span>
            </Label>
          </div>
        )}

        {/* Version footer */}
        {appVersion && (
          <div className="flex-shrink-0 pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Jam SQL Studio v{appVersion}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
